import type { StageMappings } from '@/lib/csv-import/api';

/**
 * Set (or clear) a file's stage fallback, leaving the rest of its stage mapping
 * intact.
 *
 * The invariant: **`column` and `value_mappings` survive every write.** The
 * control this replaced hardcoded `column: ''`, which was safe only because it
 * never rendered while a stage column was mapped. The consolidated control in
 * `FileDefaultsPanel` is always visible, so blanking the column would silently
 * destroy the user's stage mapping.
 *
 * Clearing the fallback on an otherwise-empty entry drops the entry entirely,
 * rather than leaving `{column: '', value_mappings: {}}` behind.
 *
 * Kept as a pure function in its own module so the invariant can be pinned
 * without driving a dropdown, and so the component file exports only components.
 */
export function applyStageFallback(
  stageMappings: StageMappings,
  fileId: string,
  targetEntityId: string | null
): StageMappings {
  const entry = stageMappings[fileId];
  const column = entry?.column ?? '';
  const valueMappings = entry?.value_mappings ?? {};

  if (targetEntityId === null) {
    if (column || Object.keys(valueMappings).length > 0) {
      return {
        ...stageMappings,
        [fileId]: { column, value_mappings: valueMappings },
      };
    }
    const { [fileId]: _removed, ...rest } = stageMappings;
    return rest;
  }

  return {
    ...stageMappings,
    [fileId]: {
      column,
      value_mappings: valueMappings,
      default_stage: targetEntityId,
    },
  };
}
